import type { ProtocolRecord } from "Order/Modules/MorningCheck/Api/Query/useOrderProtocolsQuery";
import type { PartnershipRecord } from "Order/Modules/MorningCheck/Api/Query/usePartnershipRecordsQuery";
import { OrderPaths } from "Order/Routes/Routing";
import type {
	ActionTypeFilter,
	PartnershipRecordsFilters,
	SortOrder,
	StatusFilter,
} from "Partner/Modules/PartnershipRecords/Api/Query/useAllPartnershipRecordsQuery";
import {
	ALL_PARTNERSHIP_RECORDS_KEY,
	useAllPartnershipRecordsQuery,
} from "Partner/Modules/PartnershipRecords/Api/Query/useAllPartnershipRecordsQuery";
import {
	ALL_PROTOCOLS_KEY,
	type ProtocolStatusFilter,
	type ProtocolsFilters,
	type ProtocolTypeFilter,
	useAllProtocolsQuery,
} from "Partner/Modules/PartnershipRecords/Api/Query/useAllProtocolsQuery";
import {
	ORDER_PARTNER_NAMES_KEY,
	useOrderPartnerNames,
} from "Partner/Modules/PartnershipRecords/Api/Query/useOrderPartnerNames";
import { PageContainer } from "Shared/Components/Container";
import { Loading } from "Shared/Components/Loading/Loading";
import AttachFile from "@mui/icons-material/AttachFile";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import NavigateNext from "@mui/icons-material/NavigateNext";
import Refresh from "@mui/icons-material/Refresh";
import {
	Alert,
	Chip,
	FormControl,
	IconButton,
	InputLabel,
	Link,
	MenuItem,
	Paper,
	Select,
	Stack,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tabs,
	Tooltip,
	Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { type FC, useState } from "react";
import { Link as RouterLink } from "react-router";

dayjs.extend(relativeTime);

const ACTION_TYPE_LABELS: Record<string, string> = {
	connection: "Connection",
	job_start: "Job Start",
	completion: "Completion",
	examination: "Examination",
};

const ACTION_LABELS: Record<string, string> = {
	connected: "Connected",
	not_connected: "Not Connected",
	started: "Started",
	not_started: "Not Started",
	finished: "Finished",
	not_finished: "Not Finished",
	examined: "Examined",
	not_examined: "Not Examined",
};

const POSITIVE_ACTIONS = new Set([
	"connected",
	"started",
	"finished",
	"examined",
]);

function getFilePublicUrl(filePath: string): string {
	return `https://sovmasdqgraztfwatuwb.supabase.co/storage/v1/object/public/partnership-files/${filePath}`;
}

function hasPartnerNote(record: PartnershipRecord): boolean {
	if (record.note_for_adam) return true;
	if (!record.reason) return false;
	if (record.action_type === "examination") {
		try {
			const parsed = JSON.parse(record.reason);
			return !!parsed?.description;
		} catch {
			return !!record.reason;
		}
	}
	return true;
}

function formatReason(record: PartnershipRecord): string | null {
	const noteSuffix = record.note_for_adam
		? `Note for Adam: ${record.note_for_adam}`
		: null;

	if (!record.reason) return noteSuffix;
	if (record.action_type === "examination") {
		let base: string | null;
		try {
			const parsed = JSON.parse(record.reason);
			const parts: string[] = [];
			if (parsed.description) parts.push(parsed.description);
			if (parsed.realizationDate)
				parts.push(
					`Realization: ${dayjs(parsed.realizationDate).format("DD MMM YYYY")}`,
				);
			if (parsed.priceAdjusted && parsed.newPrice)
				parts.push(`New price: ${parsed.newPrice}`);
			base = parts.join(" | ") || null;
		} catch {
			base = record.reason;
		}
		return [base, noteSuffix].filter(Boolean).join(" | ") || null;
	}
	return [record.reason, noteSuffix].filter(Boolean).join(" | ");
}

/* ─── Protocol status helpers ─── */

const PROTOCOL_STATUS_LABELS: Record<string, string> = {
	draft: "Draft",
	sent: "Sent (Awaiting Customer)",
	accepted: "Customer Accepted",
	declined: "Customer Declined",
};

const PROTOCOL_STATUS_COLOR: Record<
	string,
	"success" | "warning" | "error" | "default"
> = {
	accepted: "success",
	sent: "warning",
	declined: "error",
	draft: "default",
};

/* ─── Page component ─── */

export const PartnershipRecordsPage = () => {
	const [activeTab, setActiveTab] = useState(0);

	return (
		<PageContainer
			title="Morning Check"
			description="All morning check records from the partner app"
		>
			<Stack
				direction="row"
				justifyContent="space-between"
				alignItems="center"
				mb={2}
			>
				<Stack direction="row" alignItems="center" gap={1}>
					<Typography variant="h2">Morning Check</Typography>
					<Tooltip title="All morning check actions across orders. Auto-refreshes every 30 seconds.">
						<InfoOutlined fontSize="small" color="action" />
					</Tooltip>
				</Stack>
			</Stack>

			<Tabs
				value={activeTab}
				onChange={(_, v) => setActiveTab(v)}
				sx={{ mb: 2 }}
			>
				<Tab label="Partnership Records" />
				<Tab label="Digital Protocols" />
			</Tabs>

			{activeTab === 0 && <PartnershipRecordsTab />}
			{activeTab === 1 && <ProtocolsTab />}
		</PageContainer>
	);
};

/* ─── Tab 1: Legacy Supabase partnership records ─── */

const PartnershipRecordsTab = () => {
	const [page, setPage] = useState(0);
	const [filters, setFilters] = useState<PartnershipRecordsFilters>({
		actionType: "",
		status: "",
		sortOrder: "newest",
	});
	const queryClient = useQueryClient();

	const { data, isLoading, isError, error } = useAllPartnershipRecordsQuery(
		page,
		filters,
	);
	const records = data?.records ?? [];
	const orderIds = [...new Set(records.map((record) => record.order_id))];
	const { data: partnerNames = new Map() } = useOrderPartnerNames(orderIds);

	const handleRefresh = () => {
		queryClient.invalidateQueries({
			queryKey: [ALL_PARTNERSHIP_RECORDS_KEY],
		});
		queryClient.invalidateQueries({ queryKey: [ORDER_PARTNER_NAMES_KEY] });
	};

	const handleFilterChange = (
		key: keyof PartnershipRecordsFilters,
		value: string,
	) => {
		setFilters((prev) => ({ ...prev, [key]: value }));
		setPage(0);
	};

	const totalCount = data?.totalCount ?? 0;
	const pageSize = data?.pageSize ?? 50;
	const totalPages = Math.ceil(totalCount / pageSize);

	return (
		<>
			{/* Filters */}
			<Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
				<Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
					<FormControl size="small" sx={{ minWidth: 160 }}>
						<InputLabel>Action Type</InputLabel>
						<Select
							value={filters.actionType}
							label="Action Type"
							onChange={(e) =>
								handleFilterChange(
									"actionType",
									e.target.value as ActionTypeFilter,
								)
							}
						>
							<MenuItem value="">All</MenuItem>
							<MenuItem value="connection">Connection</MenuItem>
							<MenuItem value="job_start">Job Start</MenuItem>
							<MenuItem value="completion">Completion</MenuItem>
							<MenuItem value="examination">Examination</MenuItem>
						</Select>
					</FormControl>

					<FormControl size="small" sx={{ minWidth: 140 }}>
						<InputLabel>Status</InputLabel>
						<Select
							value={filters.status}
							label="Status"
							onChange={(e) =>
								handleFilterChange("status", e.target.value as StatusFilter)
							}
						>
							<MenuItem value="">All</MenuItem>
							<MenuItem value="positive">Positive</MenuItem>
							<MenuItem value="negative">Negative</MenuItem>
						</Select>
					</FormControl>

					<FormControl size="small" sx={{ minWidth: 140 }}>
						<InputLabel>Sort By</InputLabel>
						<Select
							value={filters.sortOrder}
							label="Sort By"
							onChange={(e) =>
								handleFilterChange("sortOrder", e.target.value as SortOrder)
							}
						>
							<MenuItem value="newest">Newest First</MenuItem>
							<MenuItem value="oldest">Oldest First</MenuItem>
						</Select>
					</FormControl>

					{(filters.actionType || filters.status) && (
						<Chip
							label="Clear filters"
							size="small"
							variant="outlined"
							onDelete={() =>
								setFilters((prev) => ({
									...prev,
									actionType: "",
									status: "",
								}))
							}
							onClick={() =>
								setFilters((prev) => ({
									...prev,
									actionType: "",
									status: "",
								}))
							}
						/>
					)}

					<Stack direction="row" alignItems="center" gap={1} ml="auto">
						<Typography variant="body2" color="text.secondary">
							{totalCount} records
						</Typography>
						<Tooltip title="Refresh now">
							<IconButton onClick={handleRefresh} size="small">
								<Refresh fontSize="small" />
							</IconButton>
						</Tooltip>
					</Stack>
				</Stack>
			</Paper>

			{isLoading && (
				<Stack
					direction="column"
					justifyContent="center"
					alignItems="center"
					spacing={2}
					sx={{ py: 4 }}
				>
					<Loading />
					<Typography variant="body2" color="text.secondary">
						Loading morning check records...
					</Typography>
				</Stack>
			)}

			{isError && (
				<Alert severity="error" sx={{ mt: 1 }}>
					Failed to load records
					{error instanceof Error ? `: ${error.message}` : ""}
				</Alert>
			)}

			{!isLoading && !isError && records.length === 0 && (
				<Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
					<Typography variant="body1" color="text.secondary" gutterBottom>
						No records found
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{filters.actionType || filters.status
							? "Try adjusting your filters."
							: "Records will appear here once partners submit actions."}
					</Typography>
				</Paper>
			)}

			{!isLoading && !isError && records.length > 0 && (
				<>
					<TableContainer component={Paper} variant="outlined">
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Order</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Partner</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Action Type</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Country</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Note / Reason</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Files</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{records.map((record) => (
									<RecordRow
										key={record.id}
										record={record}
										partnerName={partnerNames.get(record.order_id)}
									/>
								))}
							</TableBody>
						</Table>
					</TableContainer>

					{totalPages > 1 && (
						<Pagination
							page={page}
							totalPages={totalPages}
							onPageChange={setPage}
						/>
					)}
				</>
			)}
		</>
	);
};

/* ─── Tab 2: Digital protocols ─── */

const ProtocolsTab = () => {
	const [page, setPage] = useState(0);
	const [filters, setFilters] = useState<ProtocolsFilters>({
		type: "",
		status: "",
	});
	const queryClient = useQueryClient();

	const { data, isLoading, isError, error } = useAllProtocolsQuery(
		page,
		filters,
	);

	const handleRefresh = () => {
		queryClient.invalidateQueries({ queryKey: [ALL_PROTOCOLS_KEY] });
	};

	const handleFilterChange = (key: keyof ProtocolsFilters, value: string) => {
		setFilters((prev) => ({ ...prev, [key]: value }));
		setPage(0);
	};

	const records = data?.records ?? [];
	const totalCount = data?.totalCount ?? 0;
	const pageSize = data?.pageSize ?? 50;
	const totalPages = Math.ceil(totalCount / pageSize);

	return (
		<>
			<Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
				<Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
					<FormControl size="small" sx={{ minWidth: 160 }}>
						<InputLabel>Protocol Type</InputLabel>
						<Select
							value={filters.type}
							label="Protocol Type"
							onChange={(e) =>
								handleFilterChange("type", e.target.value as ProtocolTypeFilter)
							}
						>
							<MenuItem value="">All</MenuItem>
							<MenuItem value="start">Start</MenuItem>
							<MenuItem value="handover">Handover</MenuItem>
						</Select>
					</FormControl>

					<FormControl size="small" sx={{ minWidth: 160 }}>
						<InputLabel>Status</InputLabel>
						<Select
							value={filters.status}
							label="Status"
							onChange={(e) =>
								handleFilterChange(
									"status",
									e.target.value as ProtocolStatusFilter,
								)
							}
						>
							<MenuItem value="">All</MenuItem>
							<MenuItem value="draft">Draft</MenuItem>
							<MenuItem value="sent">Sent (Awaiting Customer)</MenuItem>
							<MenuItem value="accepted">Customer Accepted</MenuItem>
							<MenuItem value="declined">Customer Declined</MenuItem>
						</Select>
					</FormControl>

					{(filters.type || filters.status) && (
						<Chip
							label="Clear filters"
							size="small"
							variant="outlined"
							onDelete={() => setFilters({ type: "", status: "" })}
							onClick={() => setFilters({ type: "", status: "" })}
						/>
					)}

					<Stack direction="row" alignItems="center" gap={1} ml="auto">
						<Typography variant="body2" color="text.secondary">
							{totalCount} protocols
						</Typography>
						<Tooltip title="Refresh now">
							<IconButton onClick={handleRefresh} size="small">
								<Refresh fontSize="small" />
							</IconButton>
						</Tooltip>
					</Stack>
				</Stack>
			</Paper>

			{isLoading && (
				<Stack
					direction="column"
					justifyContent="center"
					alignItems="center"
					spacing={2}
					sx={{ py: 4 }}
				>
					<Loading />
					<Typography variant="body2" color="text.secondary">
						Loading protocol records...
					</Typography>
				</Stack>
			)}

			{isError && (
				<Alert severity="error" sx={{ mt: 1 }}>
					Failed to load protocols
					{error instanceof Error ? `: ${error.message}` : ""}
				</Alert>
			)}

			{!isLoading && !isError && records.length === 0 && (
				<Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
					<Typography variant="body1" color="text.secondary" gutterBottom>
						No protocols found
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{filters.type || filters.status
							? "Try adjusting your filters."
							: "Digital protocol records will appear once partners submit start/handover protocols."}
					</Typography>
				</Paper>
			)}

			{!isLoading && !isError && records.length > 0 && (
				<>
					<TableContainer component={Paper} variant="outlined">
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Order</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Partner Sent</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>
										Customer Decision
									</TableCell>
									<TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{records.map((record) => (
									<ProtocolRow key={record.id} record={record} />
								))}
							</TableBody>
						</Table>
					</TableContainer>

					{totalPages > 1 && (
						<Pagination
							page={page}
							totalPages={totalPages}
							onPageChange={setPage}
						/>
					)}
				</>
			)}
		</>
	);
};

/* ─── Shared pagination ─── */

type PaginationProps = {
	page: number;
	totalPages: number;
	onPageChange: (page: number) => void;
};

const Pagination: FC<PaginationProps> = ({
	page,
	totalPages,
	onPageChange,
}) => (
	<Stack
		direction="row"
		justifyContent="center"
		alignItems="center"
		gap={1}
		mt={2}
	>
		<IconButton
			onClick={() => onPageChange(Math.max(0, page - 1))}
			disabled={page === 0}
			size="small"
		>
			<NavigateBefore />
		</IconButton>
		<Typography variant="body2" color="text.secondary">
			Page {page + 1} of {totalPages}
		</Typography>
		<IconButton
			onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
			disabled={page >= totalPages - 1}
			size="small"
		>
			<NavigateNext />
		</IconButton>
	</Stack>
);

/* ─── Row components ─── */

type RecordRowProps = {
	record: PartnershipRecord;
	partnerName?: string | null;
};

const RecordRow = ({ record, partnerName }: RecordRowProps) => {
	const isPositive = POSITIVE_ACTIONS.has(record.action);
	const withNote = hasPartnerNote(record);
	const reason = formatReason(record);

	const orangeHighlight = withNote
		? {
				backgroundColor: "rgba(237, 108, 2, 0.08)",
				borderLeft: "3px solid",
				borderLeftColor: "warning.main",
			}
		: {};

	return (
		<TableRow sx={{ ...orangeHighlight, "&:last-child td": { border: 0 } }}>
			<TableCell>
				<Tooltip title={dayjs(record.created_at).format("DD/MM/YYYY HH:mm:ss")}>
					<Typography variant="body2" noWrap>
						{dayjs(record.created_at).fromNow()}
					</Typography>
				</Tooltip>
			</TableCell>
			<TableCell>
				<Link
					component={RouterLink}
					to={OrderPaths.Detail.path(Number(record.order_id))}
					underline="hover"
					fontWeight={500}
				>
					#{record.order_id}
				</Link>
			</TableCell>
			<TableCell>
				<Typography variant="body2" noWrap>
					{partnerName ?? "—"}
				</Typography>
			</TableCell>
			<TableCell>
				<Typography variant="body2">
					{ACTION_TYPE_LABELS[record.action_type] ?? record.action_type}
				</Typography>
			</TableCell>
			<TableCell>
				<Chip
					label={
						ACTION_LABELS[record.action] ?? record.action.replace(/_/g, " ")
					}
					size="small"
					color={isPositive ? "success" : "error"}
					sx={{ textTransform: "capitalize" }}
				/>
			</TableCell>
			<TableCell>
				{record.country && (
					<Chip
						label={record.country.toUpperCase()}
						size="small"
						variant="outlined"
					/>
				)}
			</TableCell>
			<TableCell sx={{ maxWidth: 300 }}>
				{reason && (
					<Tooltip title={reason}>
						<Typography
							variant="body2"
							color={withNote ? "warning.dark" : "text.secondary"}
							fontWeight={withNote ? 500 : 400}
							noWrap
						>
							{reason}
						</Typography>
					</Tooltip>
				)}
			</TableCell>
			<TableCell>
				<FileLinks record={record} />
			</TableCell>
		</TableRow>
	);
};

type ProtocolRowProps = { record: ProtocolRecord };

const ProtocolRow: FC<ProtocolRowProps> = ({ record }) => {
	const statusColor = PROTOCOL_STATUS_COLOR[record.status] ?? "default";
	const hasNotes = !!record.priceAdjustmentNotes || !!record.customerComment;

	const noteHighlight = hasNotes
		? {
				backgroundColor: "rgba(237, 108, 2, 0.08)",
				borderLeft: "3px solid",
				borderLeftColor: "warning.main",
			}
		: {};

	const noteText = [
		record.priceAdjustmentNotes
			? `Partner: ${record.priceAdjustmentNotes}`
			: null,
		record.customerComment ? `Customer: ${record.customerComment}` : null,
	]
		.filter(Boolean)
		.join(" | ");

	return (
		<TableRow sx={{ ...noteHighlight, "&:last-child td": { border: 0 } }}>
			<TableCell>
				<Tooltip
					title={dayjs(record.dateCreated).format("DD/MM/YYYY HH:mm:ss")}
				>
					<Typography variant="body2" noWrap>
						{dayjs(record.dateCreated).fromNow()}
					</Typography>
				</Tooltip>
			</TableCell>
			<TableCell>
				<Link
					component={RouterLink}
					to={OrderPaths.Detail.path(record.orderId)}
					underline="hover"
					fontWeight={500}
				>
					#{record.orderId}
				</Link>
			</TableCell>
			<TableCell>
				<Chip
					label={
						record.type === "start" ? "Start Protocol" : "Handover Protocol"
					}
					size="small"
					variant="outlined"
					color={record.type === "start" ? "info" : "secondary"}
				/>
			</TableCell>
			<TableCell>
				{record.sentAt ? (
					<Tooltip title={dayjs(record.sentAt).format("DD/MM/YYYY HH:mm:ss")}>
						<Chip
							label={dayjs(record.sentAt).format("DD/MM HH:mm")}
							size="small"
							color="success"
							variant="outlined"
						/>
					</Tooltip>
				) : (
					<Chip
						label="Not sent"
						size="small"
						variant="outlined"
						color="default"
					/>
				)}
			</TableCell>
			<TableCell>
				<Chip
					label={PROTOCOL_STATUS_LABELS[record.status] ?? record.status}
					size="small"
					color={statusColor}
					variant={record.status === "accepted" ? "filled" : "outlined"}
				/>
				{record.customerAcceptedAt && (
					<Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
						{dayjs(record.customerAcceptedAt).format("DD/MM HH:mm")}
					</Typography>
				)}
			</TableCell>
			<TableCell sx={{ maxWidth: 300 }}>
				{noteText && (
					<Tooltip title={noteText}>
						<Typography
							variant="body2"
							color="warning.dark"
							fontWeight={500}
							noWrap
						>
							{noteText}
						</Typography>
					</Tooltip>
				)}
			</TableCell>
		</TableRow>
	);
};

const FileLinks: FC<{ record: PartnershipRecord }> = ({ record }) => {
	const files: string[] = [];

	if (record.file_path) {
		files.push(record.file_path);
	}

	if (record.action_type === "examination" && record.reason) {
		try {
			const parsed = JSON.parse(record.reason);
			if (parsed?.files && Array.isArray(parsed.files)) {
				files.push(...parsed.files);
			}
		} catch {
			// ignore
		}
	}

	if (files.length === 0) return null;

	return (
		<Stack direction="row" gap={0.5} flexWrap="wrap">
			{files.map((filePath) => (
				<Chip
					key={filePath}
					icon={<AttachFile fontSize="small" />}
					label="File"
					size="small"
					component="a"
					href={getFilePublicUrl(filePath)}
					target="_blank"
					rel="noopener noreferrer"
					clickable
					variant="outlined"
					color="primary"
				/>
			))}
		</Stack>
	);
};
